// Site screens: SEO & Settings (with Tracking), Header & Footer, Specification Reference.
// Third-party UI paths ("Events Manager → Settings …") and event names stay in English in the components.
export const siteEn = {
  // SEO & Settings
  'site.settings.adminOnly': 'Only admins can change site settings.',
  'site.settings.subtitle': 'Site-wide defaults, organisation details and tracking.',
  'site.settings.save': 'Save settings',
  'site.settings.saved': 'Settings saved',
  'site.settings.primaryConfirm.title': 'Make {language} the primary language?',
  'site.settings.primaryConfirm.body':
    'Every empty translation on the site will fall back to {to} instead of {from}, and new content will require a {to} title. Page URLs do not change.',
  'site.settings.primaryConfirm.confirm': 'Change primary language',

  'site.seo.title': 'SEO Defaults',
  'site.seo.description': 'Used when a page has no SEO fields of its own.',
  'site.seo.siteName': 'Site name',
  'site.seo.titleTemplate': 'Title template',
  'site.seo.titleTemplateHint': '%s is replaced with the page title.',
  'site.seo.defaultTitle': 'Default title',
  'site.seo.defaultDescription': 'Default description',
  'site.seo.tagline': 'Tagline',
  'site.seo.shareImage': 'Default share image',
  'site.seo.twitter': 'X / Twitter handle',
  'site.seo.googleVerification': 'Google site verification',
  'site.seo.googleVerificationHint': 'The content value of the google-site-verification meta tag.',

  'site.emails.title': 'Quote request emails',
  'site.emails.description': 'Who is emailed when a quote request arrives.',
  'site.emails.autoReply': 'Send the customer a confirmation',
  'site.emails.autoReplyHint': 'A short thank-you in the language the visitor used (English, Indonesian or Chinese).',
  'site.design.title': 'Design',
  'site.design.description': 'The look of your public website.',
  'site.design.template': 'Template',
  'site.design.template.editorial': 'Editorial',
  'site.design.template.editorial.hint': 'The full design: mega menu, big footer and every kind of page section.',
  'site.design.template.landing': 'Landing',
  'site.design.template.landing.hint': 'A compact one-page style: a simple menu, a small footer and the essential page sections.',
  'site.design.template.boutique': 'Boutique',
  'site.design.template.boutique.hint': 'For jewelry, fashion and luxury retail: a centred logo, the tagline as an announcement bar, and product-first pages.',
  'site.design.template.studio': 'Studio',
  'site.design.template.studio.hint': 'For a game studio: bright and rounded, a sunburst hero, a game panel with store buttons, and the contact form inside the page.',
  'site.design.templateNote': 'Switching template changes the header, footer and which sections the page editor offers. Sections already on a page stay. The website updates after the next rebuild.',
  'site.design.logo': 'Logo',
  'site.design.logoHint': 'Shown in the header. Empty uses the default logo.',
  'site.design.favicon': 'Browser icon',
  'site.design.faviconHint': 'A square image, at least 192 px. Empty uses the default.',
  'site.design.accent': 'Accent colour',
  'site.design.accentHint': 'Links and highlights, e.g. #6b2733. Empty uses the default.',
  'site.design.accentReset': 'Use default',
  'site.local.title': 'Local search',
  'site.local.description': 'Opening hours and position for Google. Used with the address below; needs a city to be published.',
  'site.local.type': 'Business type',
  'site.local.typeHint': 'A schema.org type such as LocalBusiness, Store, Restaurant or ProfessionalService.',
  'site.local.priceRange': 'Price range',
  'site.local.lat': 'Latitude',
  'site.local.lng': 'Longitude',
  'site.local.geoHint': 'Right-click your place in Google Maps to copy both numbers. Leave empty to skip.',
  'site.local.hours': 'Opening hours',
  'site.local.open': 'Open',
  'site.local.opens': 'Opens',
  'site.local.closes': 'Closes',
  'site.local.closed': 'Closed',
  'site.local.copyDown': 'Copy Monday to all open days',
  'site.local.day.mon': 'Monday',
  'site.local.day.tue': 'Tuesday',
  'site.local.day.wed': 'Wednesday',
  'site.local.day.thu': 'Thursday',
  'site.local.day.fri': 'Friday',
  'site.local.day.sat': 'Saturday',
  'site.local.day.sun': 'Sunday',
  'site.form.title': 'Quote form fields',
  'site.form.description': 'Choose which questions the contact form asks. Name and email are always asked.',
  'site.form.locked': 'Your plan uses the standard form. Upgrade to choose the fields.',
  'site.form.mode.hidden': 'Hidden',
  'site.form.mode.optional': 'Optional',
  'site.form.mode.required': 'Required',
  'site.form.field.company': 'Company',
  'site.form.field.role': 'Role',
  'site.form.field.country': 'Country',
  'site.form.field.productInterest': 'Interest',
  'site.form.field.quantity': 'Quantity',
  'site.form.field.timeline': 'Timeline',
  'site.form.field.message': 'Message',
  'site.emails.recipients': 'Notify these addresses',
  'site.emails.hintFallback': 'One address per line, up to 10. When empty, {email} is used.',
  'site.emails.hintNoFallback': 'One address per line, up to 10. When empty and no organisation email is set, no email is sent.',

  'site.org.title': 'Organisation',
  'site.org.description': 'Shown in the footer and in Organization structured data.',
  'site.org.legalName': 'Legal name',
  'site.org.foundingYear': 'Founding year',
  'site.org.email': 'Email',
  'site.org.phone': 'Phone',
  'site.org.street': 'Street',
  'site.org.city': 'City',
  'site.org.region': 'Region / province',
  'site.org.postalCode': 'Postal code',
  'site.org.country': 'Country',
  'site.org.socials': 'Social profiles',
  'site.org.addSocial': '+ Profile',
  'site.org.platform': 'Platform',
  'site.org.url': 'URL',

  'site.languages.title': 'Languages',
  'site.languages.description': 'The primary language is the fallback for every empty translation — text and images.',
  'site.languages.primary': 'Primary language',
  'site.languages.primaryHint': 'Required for every title. Changing it never changes page URLs.',
  'site.languages.published': 'Published languages',
  'site.languages.prefixHint': 'Each published language gets its own URL prefix (/en, /id, /zh, /vi, /ja).',
  'site.languages.switching': 'Switching the primary language from {from} to {to}',
  'site.languages.checking': 'Checking {language} translations…',
  'site.languages.allTranslated': 'All content already has {language} titles.',
  'site.languages.someMissing': 'Some content has no {language} title yet. It stays visible (falling back to another language) but should be translated:',
  'site.languages.translatedCount': '{translated} / {total} translated',
  'site.type.pages': 'Pages',
  'site.type.product-categories': 'Categories',
  'site.type.products': 'Products',
  'site.type.industries': 'Industries',
  'site.type.articles': 'Articles',

  // Tracking
  'site.tracking.title': 'Tracking',
  'site.tracking.description': 'Analytics and advertising pixels, plus the server-side copies of each lead. Saved separately from the settings above.',
  'site.tracking.intro':
    'Every quote request sends a Lead from the browser and, with a token, a second copy from our server with the same event ID so platforms count it once. Tokens and secrets are encrypted, never shown again, and never leave the server.',
  'site.tracking.saved': 'Tracking saved',
  'site.tracking.save': 'Save tracking',
  'site.tracking.discard': 'Discard',
  'site.tracking.saveFirst': 'Save before sending a test event.',
  'site.tracking.enabled': 'Enabled',
  'site.tracking.testAccepted': '{platform}: test event accepted',
  'site.tracking.sendTest': 'Send test event',
  'site.tracking.sending': 'Sending…',
  'site.tracking.testEventCode': 'Test event code',
  'site.tracking.secret.unchanged': '•••••••• (unchanged)',
  'site.tracking.secret.paste': 'Paste the secret',
  'site.tracking.secret.willRemove': 'Will be removed on save',
  'site.tracking.secret.undo': 'Undo',
  'site.tracking.secret.configured': 'Configured · ends …{last4}',
  'site.tracking.secret.remove': 'Remove',
  'site.tracking.secret.notConfigured': 'Not configured',
  'site.tracking.ga.description': 'Page views, product views, contact clicks and generate_lead.',
  'site.tracking.ga.measurementId': 'Measurement ID',
  'site.tracking.ga.apiSecret': 'Measurement Protocol API secret',
  'site.tracking.ga.apiSecretHint': 'GA4 → Admin → Data streams → Measurement Protocol API secrets. Needed for the server copy.',
  'site.tracking.meta.pixelId': 'Pixel ID',
  'site.tracking.meta.pixelIdHint': 'Digits only, from Events Manager.',
  'site.tracking.meta.accessToken': 'Conversions API access token',
  'site.tracking.meta.testHint': 'From Events Manager → Test events. Required for “Send test event”.',
  'site.tracking.tiktok.pixelCode': 'Pixel code',
  'site.tracking.tiktok.accessToken': 'Events API access token',
  'site.tracking.tiktok.accessTokenHint': 'Events Manager → your pixel → Settings → Generate access token.',
  'site.tracking.tiktok.testHint': 'From the Test events tab. Required for “Send test event”.',
  'site.tracking.deliveries.title': 'Recent server deliveries',
  'site.tracking.deliveries.refresh': 'Refresh',
  'site.tracking.deliveries.refreshing': 'Refreshing…',
  'site.tracking.deliveries.empty': 'Nothing sent yet. Deliveries appear here after a quote request or a test event.',
  'site.tracking.deliveries.when': 'When',
  'site.tracking.deliveries.platform': 'Platform',
  'site.tracking.deliveries.event': 'Event',
  'site.tracking.deliveries.result': 'Result',
  'site.tracking.deliveries.test': 'test',
  'site.tracking.deliveries.accepted': 'Accepted',
  'site.tracking.code.notConfigured': 'Not configured: add the ID and token, then enable it',
  'site.tracking.code.noTestEventCode': 'Add a test event code first',
  'site.tracking.code.network': 'Could not reach the platform',
  'site.tracking.code.timeout': 'Timed out',
  'site.tracking.code.rejectedToken': 'Rejected: check the access token',
  'site.tracking.code.rejectedId': 'Rejected: check the ID and event code',
  'site.tracking.code.tiktok': 'TikTok rejected the event ({code})',
  'site.tracking.code.ga4Validation': 'GA4 validation failed: check the measurement ID and API secret',
  'site.tracking.code.failed': 'Failed',

  // Header & Footer
  'site.nav.subtitle': 'Menus and footer content shown on every page.',
  'site.nav.saved': 'Navigation saved',
  'site.nav.label': 'Label',
  'site.nav.link': 'Link',
  'site.nav.addLink': '+ Link',
  'site.nav.header': 'Header Menu',
  'site.nav.menuItem': 'Menu item {n}',
  'site.nav.mega': 'Mega menu',
  'site.nav.megaHint': 'Lists product categories and industries automatically. Dropdown links below become its last column.',
  'site.nav.megaHeading': 'Last column heading',
  'site.nav.megaHeadingPlaceholder': 'Before you order',
  'site.nav.dropdownLinks': 'Dropdown links',
  'site.nav.addDropdownLink': '+ Dropdown link',
  'site.nav.addMenuItem': '+ Menu item',
  'site.nav.cta': 'Call-to-action button',
  'site.nav.addCta': '+ Add button',
  'site.nav.footer': 'Footer',
  'site.nav.eyebrow': 'Eyebrow',
  'site.nav.headline': 'Headline',
  'site.nav.headlineHint': 'Wrap words in *asterisks* for the gold italic accent.',
  'site.nav.columnTitle': 'Column {n} title',
  'site.nav.removeColumn': 'Remove column',
  'site.nav.addColumn': '+ Column',
  'site.nav.legalLinks': 'Legal links',

  // Specification Reference
  'site.spec.subtitle': 'Copy shown on every product, category and industry page.',
  'site.spec.saved': 'Specification reference saved',
  'site.spec.terms.title': 'Terms for this specification',
  'site.spec.terms.description': 'Shown under a page’s specification table. A term with no groups ticked applies to every page that has swatch groups.',
  'site.spec.term': 'Term',
  'site.spec.text': 'Text',
  'site.spec.addTerm': '+ Term',
  'site.spec.group.rigid': 'Rigid boxes',
  'site.spec.group.label': 'Labels',
  'site.spec.group.carton': 'Folding cartons',
  'site.spec.group.corrugated': 'Corrugated',
  'site.spec.group.bag': 'Paper bags',
  'site.spec.group.nonwoven': 'Non-woven bags',
  'site.spec.group.tube': 'Paper tubes',
  'site.spec.group.component': 'Printed components',
  'site.spec.cost.title': 'What drives the cost',
  'site.spec.cost.description': 'The cells under “What drives the cost” on every landing page.',
  'site.spec.cost.driver': 'Driver',
  'site.spec.cost.explanation': 'Explanation',
  'site.spec.cost.add': '+ Cost driver',
} as const

export const siteId: Record<keyof typeof siteEn, string> = {
  // SEO & Settings
  'site.settings.adminOnly': 'Hanya admin yang dapat mengubah pengaturan situs.',
  'site.settings.subtitle': 'Setelan bawaan seluruh situs, detail organisasi, dan pelacakan.',
  'site.settings.save': 'Simpan pengaturan',
  'site.settings.saved': 'Pengaturan disimpan',
  'site.settings.primaryConfirm.title': 'Jadikan {language} bahasa utama?',
  'site.settings.primaryConfirm.body':
    'Setiap terjemahan yang kosong di situs akan memakai {to}, bukan {from}, dan konten baru wajib memiliki judul dalam {to}. URL halaman tidak berubah.',
  'site.settings.primaryConfirm.confirm': 'Ubah bahasa utama',

  'site.seo.title': 'Bawaan SEO',
  'site.seo.description': 'Dipakai saat halaman tidak memiliki kolom SEO sendiri.',
  'site.seo.siteName': 'Nama situs',
  'site.seo.titleTemplate': 'Templat judul',
  'site.seo.titleTemplateHint': '%s diganti dengan judul halaman.',
  'site.seo.defaultTitle': 'Judul bawaan',
  'site.seo.defaultDescription': 'Deskripsi bawaan',
  'site.seo.tagline': 'Tagline',
  'site.seo.shareImage': 'Gambar bagikan bawaan',
  'site.seo.twitter': 'Akun X / Twitter',
  'site.seo.googleVerification': 'Verifikasi situs Google',
  'site.seo.googleVerificationHint': 'Nilai content dari meta tag google-site-verification.',

  'site.emails.title': 'Email permintaan penawaran',
  'site.emails.description': 'Siapa yang menerima email saat permintaan penawaran masuk.',
  'site.emails.autoReply': 'Kirim konfirmasi ke pelanggan',
  'site.emails.autoReplyHint': 'Ucapan terima kasih singkat dalam bahasa yang digunakan pengunjung (Inggris, Indonesia, atau Mandarin).',
  'site.design.title': 'Tampilan',
  'site.design.description': 'Tampilan situs web publik Anda.',
  'site.design.template': 'Templat',
  'site.design.template.editorial': 'Editorial',
  'site.design.template.editorial.hint': 'Desain lengkap: mega menu, footer besar, dan semua jenis bagian halaman.',
  'site.design.template.landing': 'Landing',
  'site.design.template.landing.hint': 'Gaya satu halaman yang ringkas: menu sederhana, footer kecil, dan bagian halaman yang esensial.',
  'site.design.template.boutique': 'Boutique',
  'site.design.template.boutique.hint': 'Untuk perhiasan, fesyen, dan ritel mewah: logo di tengah, tagline sebagai bilah pengumuman, dan halaman yang mengutamakan produk.',
  'site.design.template.studio': 'Studio',
  'site.design.template.studio.hint': 'Untuk studio game: cerah dan membulat, hero bergaya sinar matahari, panel game dengan tombol toko, dan formulir kontak langsung di halaman.',
  'site.design.templateNote': 'Berganti templat mengubah header, footer, dan bagian yang ditawarkan editor halaman. Bagian yang sudah ada di halaman tetap. Situs web diperbarui setelah build berikutnya.',
  'site.design.logo': 'Logo',
  'site.design.logoHint': 'Tampil di header. Kosong memakai logo bawaan.',
  'site.design.favicon': 'Ikon browser',
  'site.design.faviconHint': 'Gambar persegi, minimal 192 px. Kosong memakai bawaan.',
  'site.design.accent': 'Warna aksen',
  'site.design.accentHint': 'Tautan dan sorotan, mis. #6b2733. Kosong memakai bawaan.',
  'site.design.accentReset': 'Pakai bawaan',
  'site.local.title': 'Pencarian lokal',
  'site.local.description': 'Jam buka dan posisi untuk Google. Dipakai bersama alamat di bawah; perlu kota agar terbit.',
  'site.local.type': 'Jenis usaha',
  'site.local.typeHint': 'Tipe schema.org seperti LocalBusiness, Store, Restaurant, atau ProfessionalService.',
  'site.local.priceRange': 'Kisaran harga',
  'site.local.lat': 'Lintang',
  'site.local.lng': 'Bujur',
  'site.local.geoHint': 'Klik kanan lokasi Anda di Google Maps untuk menyalin kedua angka. Kosongkan untuk melewati.',
  'site.local.hours': 'Jam buka',
  'site.local.open': 'Buka',
  'site.local.opens': 'Buka pukul',
  'site.local.closes': 'Tutup pukul',
  'site.local.closed': 'Tutup',
  'site.local.copyDown': 'Salin hari Senin ke semua hari buka',
  'site.local.day.mon': 'Senin',
  'site.local.day.tue': 'Selasa',
  'site.local.day.wed': 'Rabu',
  'site.local.day.thu': 'Kamis',
  'site.local.day.fri': 'Jumat',
  'site.local.day.sat': 'Sabtu',
  'site.local.day.sun': 'Minggu',
  'site.form.title': 'Kolom formulir penawaran',
  'site.form.description': 'Pilih pertanyaan yang diajukan formulir kontak. Nama dan email selalu ditanyakan.',
  'site.form.locked': 'Paket Anda memakai formulir standar. Tingkatkan paket untuk memilih kolom.',
  'site.form.mode.hidden': 'Disembunyikan',
  'site.form.mode.optional': 'Opsional',
  'site.form.mode.required': 'Wajib',
  'site.form.field.company': 'Perusahaan',
  'site.form.field.role': 'Jabatan',
  'site.form.field.country': 'Negara',
  'site.form.field.productInterest': 'Minat',
  'site.form.field.quantity': 'Jumlah',
  'site.form.field.timeline': 'Jangka waktu',
  'site.form.field.message': 'Pesan',
  'site.emails.recipients': 'Beri tahu alamat berikut',
  'site.emails.hintFallback': 'Satu alamat per baris, maksimal 10. Jika kosong, {email} yang dipakai.',
  'site.emails.hintNoFallback': 'Satu alamat per baris, maksimal 10. Jika kosong dan email organisasi belum diisi, tidak ada email yang dikirim.',

  'site.org.title': 'Organisasi',
  'site.org.description': 'Ditampilkan di footer dan pada data terstruktur Organization.',
  'site.org.legalName': 'Nama badan hukum',
  'site.org.foundingYear': 'Tahun berdiri',
  'site.org.email': 'Email',
  'site.org.phone': 'Telepon',
  'site.org.street': 'Jalan',
  'site.org.city': 'Kota',
  'site.org.region': 'Provinsi / wilayah',
  'site.org.postalCode': 'Kode pos',
  'site.org.country': 'Negara',
  'site.org.socials': 'Profil media sosial',
  'site.org.addSocial': '+ Profil',
  'site.org.platform': 'Platform',
  'site.org.url': 'URL',

  'site.languages.title': 'Bahasa',
  'site.languages.description': 'Bahasa utama menjadi cadangan untuk setiap terjemahan yang kosong — teks dan gambar.',
  'site.languages.primary': 'Bahasa utama',
  'site.languages.primaryHint': 'Wajib untuk setiap judul. Mengubahnya tidak mengubah URL halaman.',
  'site.languages.published': 'Bahasa yang diterbitkan',
  'site.languages.prefixHint': 'Setiap bahasa yang diterbitkan memiliki awalan URL sendiri (/en, /id, /zh, /vi, /ja).',
  'site.languages.switching': 'Mengganti bahasa utama dari {from} ke {to}',
  'site.languages.checking': 'Memeriksa terjemahan {language}…',
  'site.languages.allTranslated': 'Semua konten sudah memiliki judul dalam {language}.',
  'site.languages.someMissing': 'Sebagian konten belum memiliki judul dalam {language}. Konten tetap tampil (memakai bahasa lain), tetapi sebaiknya diterjemahkan:',
  'site.languages.translatedCount': '{translated} / {total} diterjemahkan',
  'site.type.pages': 'Halaman',
  'site.type.product-categories': 'Kategori',
  'site.type.products': 'Produk',
  'site.type.industries': 'Industri',
  'site.type.articles': 'Artikel',

  // Tracking
  'site.tracking.title': 'Pelacakan',
  'site.tracking.description': 'Pixel analitik dan iklan, beserta salinan sisi server untuk setiap prospek. Disimpan terpisah dari pengaturan di atas.',
  'site.tracking.intro':
    'Setiap permintaan penawaran mengirim event Lead dari browser dan, jika token tersedia, salinan kedua dari server kami dengan ID event yang sama sehingga platform hanya menghitungnya sekali. Token dan kunci rahasia dienkripsi, tidak pernah ditampilkan lagi, dan tidak pernah keluar dari server.',
  'site.tracking.saved': 'Pelacakan disimpan',
  'site.tracking.save': 'Simpan pelacakan',
  'site.tracking.discard': 'Buang perubahan',
  'site.tracking.saveFirst': 'Simpan sebelum mengirim event uji.',
  'site.tracking.enabled': 'Aktif',
  'site.tracking.testAccepted': '{platform}: event uji diterima',
  'site.tracking.sendTest': 'Kirim event uji',
  'site.tracking.sending': 'Mengirim…',
  'site.tracking.testEventCode': 'Kode event uji',
  'site.tracking.secret.unchanged': '•••••••• (tidak diubah)',
  'site.tracking.secret.paste': 'Tempel kunci rahasia',
  'site.tracking.secret.willRemove': 'Akan dihapus saat disimpan',
  'site.tracking.secret.undo': 'Urungkan',
  'site.tracking.secret.configured': 'Sudah diatur · berakhir …{last4}',
  'site.tracking.secret.remove': 'Hapus',
  'site.tracking.secret.notConfigured': 'Belum diatur',
  'site.tracking.ga.description': 'Tampilan halaman, tampilan produk, klik kontak, dan generate_lead.',
  'site.tracking.ga.measurementId': 'ID Pengukuran',
  'site.tracking.ga.apiSecret': 'Kunci rahasia API Measurement Protocol',
  'site.tracking.ga.apiSecretHint': 'GA4 → Admin → Data streams → Measurement Protocol API secrets. Diperlukan untuk salinan server.',
  'site.tracking.meta.pixelId': 'ID Pixel',
  'site.tracking.meta.pixelIdHint': 'Hanya angka, dari Events Manager.',
  'site.tracking.meta.accessToken': 'Token akses Conversions API',
  'site.tracking.meta.testHint': 'Dari Events Manager → Test events. Wajib untuk “Kirim event uji”.',
  'site.tracking.tiktok.pixelCode': 'Kode Pixel',
  'site.tracking.tiktok.accessToken': 'Token akses Events API',
  'site.tracking.tiktok.accessTokenHint': 'Events Manager → pixel Anda → Settings → Generate access token.',
  'site.tracking.tiktok.testHint': 'Dari tab Test events. Wajib untuk “Kirim event uji”.',
  'site.tracking.deliveries.title': 'Pengiriman server terbaru',
  'site.tracking.deliveries.refresh': 'Muat ulang',
  'site.tracking.deliveries.refreshing': 'Memuat ulang…',
  'site.tracking.deliveries.empty': 'Belum ada yang terkirim. Pengiriman akan muncul di sini setelah ada permintaan penawaran atau event uji.',
  'site.tracking.deliveries.when': 'Waktu',
  'site.tracking.deliveries.platform': 'Platform',
  'site.tracking.deliveries.event': 'Event',
  'site.tracking.deliveries.result': 'Hasil',
  'site.tracking.deliveries.test': 'uji',
  'site.tracking.deliveries.accepted': 'Diterima',
  'site.tracking.code.notConfigured': 'Belum diatur: tambahkan ID dan token, lalu aktifkan',
  'site.tracking.code.noTestEventCode': 'Tambahkan kode event uji terlebih dahulu',
  'site.tracking.code.network': 'Tidak dapat menjangkau platform',
  'site.tracking.code.timeout': 'Waktu habis',
  'site.tracking.code.rejectedToken': 'Ditolak: periksa token akses',
  'site.tracking.code.rejectedId': 'Ditolak: periksa ID dan kode event',
  'site.tracking.code.tiktok': 'TikTok menolak event ({code})',
  'site.tracking.code.ga4Validation': 'Validasi GA4 gagal: periksa ID pengukuran dan kunci rahasia API',
  'site.tracking.code.failed': 'Gagal',

  // Header & Footer
  'site.nav.subtitle': 'Menu dan konten footer yang tampil di setiap halaman.',
  'site.nav.saved': 'Navigasi disimpan',
  'site.nav.label': 'Label',
  'site.nav.link': 'Tautan',
  'site.nav.addLink': '+ Tautan',
  'site.nav.header': 'Menu Header',
  'site.nav.menuItem': 'Item menu {n}',
  'site.nav.mega': 'Mega menu',
  'site.nav.megaHint': 'Menampilkan kategori produk dan industri secara otomatis. Tautan dropdown di bawah menjadi kolom terakhirnya.',
  'site.nav.megaHeading': 'Judul kolom terakhir',
  'site.nav.megaHeadingPlaceholder': 'Sebelum memesan',
  'site.nav.dropdownLinks': 'Tautan dropdown',
  'site.nav.addDropdownLink': '+ Tautan dropdown',
  'site.nav.addMenuItem': '+ Item menu',
  'site.nav.cta': 'Tombol ajakan bertindak',
  'site.nav.addCta': '+ Tambah tombol',
  'site.nav.footer': 'Footer',
  'site.nav.eyebrow': 'Teks atas (eyebrow)',
  'site.nav.headline': 'Judul utama',
  'site.nav.headlineHint': 'Apit kata dengan *tanda bintang* untuk aksen miring berwarna emas.',
  'site.nav.columnTitle': 'Judul kolom {n}',
  'site.nav.removeColumn': 'Hapus kolom',
  'site.nav.addColumn': '+ Kolom',
  'site.nav.legalLinks': 'Tautan legal',

  // Specification Reference
  'site.spec.subtitle': 'Teks yang tampil di setiap halaman produk, kategori, dan industri.',
  'site.spec.saved': 'Referensi spesifikasi disimpan',
  'site.spec.terms.title': 'Istilah untuk spesifikasi ini',
  'site.spec.terms.description': 'Ditampilkan di bawah tabel spesifikasi halaman. Istilah tanpa grup yang dicentang berlaku untuk semua halaman yang memiliki grup bahan.',
  'site.spec.term': 'Istilah',
  'site.spec.text': 'Teks',
  'site.spec.addTerm': '+ Istilah',
  'site.spec.group.rigid': 'Kotak rigid',
  'site.spec.group.label': 'Label',
  'site.spec.group.carton': 'Karton lipat',
  'site.spec.group.corrugated': 'Karton gelombang',
  'site.spec.group.bag': 'Tas kertas',
  'site.spec.group.nonwoven': 'Tas non-woven',
  'site.spec.group.tube': 'Tabung kertas',
  'site.spec.group.component': 'Komponen cetak',
  'site.spec.cost.title': 'Faktor penentu biaya',
  'site.spec.cost.description': 'Sel di bawah “Faktor penentu biaya” pada setiap halaman landing.',
  'site.spec.cost.driver': 'Faktor',
  'site.spec.cost.explanation': 'Penjelasan',
  'site.spec.cost.add': '+ Faktor biaya',
}
